import { atom, Atom } from 'jotai';
import { unwrap } from 'jotai/utils';

// util to make an atom unwrapped and return initial/previous data, but also provide a loading state while the atom is resolving.
export const unwrapWithLoading = <T>(
  asyncAtom: Atom<Promise<T>>,
  initialData: T,
): Atom<{ data: T; isLoading: boolean }> => {
  const stateAtom = atom(async get => {
    try {
      return { data: await get(asyncAtom), isLoading: false };
    } catch {
      return { data: initialData, isLoading: false };
    }
  });

  return unwrap(stateAtom, prev => ({ data: prev?.data ?? initialData, isLoading: true }));
};

export type Loadable<T> =
  | { state: 'loading' }
  | { state: 'hasData'; data: Awaited<T> }
  | { state: 'hasError'; error: unknown };

const LOADING = { state: 'loading' } as const;

const cache = new WeakMap<Atom<unknown>, Atom<Loadable<unknown>>>();

/** @deprecated Use `unwrap` from `jotai/utils` directly instead. */
export const loadable = <T>(anAtom: Atom<T>) => {
  const cachedAtom = cache.get(anAtom);
  if (cachedAtom) return cachedAtom as Atom<Loadable<T>>;

  const unwrappedAtom = unwrap(anAtom, () => LOADING);
  const loadableAtom = atom<Loadable<T>>(get => {
    try {
      const data = get(unwrappedAtom);
      if (data === LOADING) {
        return LOADING;
      }
      return { state: 'hasData', data: data as Awaited<T> };
    } catch (error) {
      return { state: 'hasError', error };
    }
  });

  cache.set(anAtom, loadableAtom as Atom<Loadable<unknown>>);
  return loadableAtom;
};
